import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';

// Pages - Public
import Landing from '@/pages/Landing';
import About from '@/pages/About';
import Guide from '@/pages/Guide';
import LegalPage from '@/pages/LegalPage';
import BrowseListings from '@/pages/BrowseListings';
import ListingDetail from '@/pages/ListingDetail';
import Login from '@/pages/Login';
import Register from '@/pages/Register';

// Pages - Applicant flow
import ProfileComplete from '@/pages/ProfileComplete';
import MembershipApply from '@/pages/MembershipApply';
import MembershipStatus from '@/pages/MembershipStatus';

// Pages - Member
import MemberDashboard from '@/pages/member/MemberDashboard';
import MyListings from '@/pages/member/MyListings';
import CreateListing from '@/pages/member/CreateListing';
import EditListing from '@/pages/member/EditListing';
import Enquiries from '@/pages/member/Enquiries';
import MemberProfile from '@/pages/member/MemberProfile';
import MemberTransactions from '@/pages/member/Transactions';

// Pages - Admin
import AdminOverview from '@/pages/admin/AdminOverview';
import Applications from '@/pages/admin/Applications';
import Members from '@/pages/admin/Members';
import MemberDetail from '@/pages/admin/MemberDetail';
import AllListings from '@/pages/admin/AllListings';
import Categories from '@/pages/admin/Categories';
import ListingTypes from '@/pages/admin/ListingTypes';
import Config from '@/pages/admin/Config';
import SiteContent from '@/pages/admin/SiteContent';
import NotificationManager from '@/pages/admin/NotificationManager';
import Conversations from '@/pages/admin/Conversations';
import BlockedWords from '@/pages/admin/BlockedWords';
import AdminTransactions from '@/pages/admin/Transactions';

import AuthCallback from '@/pages/AuthCallback';
import AccessDenied from '@/pages/AccessDenied';
import NotFound from '@/pages/NotFound';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import PaymentSuccess from '@/pages/PaymentSuccess';
import PaymentCancel from '@/pages/PaymentCancel';

// Route guards
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1C1C1C] border-t-transparent" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AuthRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1C1C1C] border-t-transparent" /></div>;
  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'member') return <Navigate to="/dashboard" replace />;
    if (!user.profile_completed) return <Navigate to="/profile/complete" replace />;
    if (user.membership_status === 'none') return <Navigate to="/membership/apply" replace />;
    return <Navigate to="/membership/status" replace />;
  }
  return children;
};

const MemberRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1C1C1C] border-t-transparent" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return children; // Admin can access member routes
  if (user.role !== 'member') return <Navigate to="/access-denied" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1C1C1C] border-t-transparent" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/access-denied" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/about" element={<About />} />
          <Route path="/guide" element={<Guide />} />
          <Route path="/privacy" element={<LegalPage page="privacy" />} />
          <Route path="/terms" element={<LegalPage page="terms" />} />
          <Route path="/browse" element={<BrowseListings />} />
          <Route path="/listings/:id" element={<ListingDetail />} />

          {/* Auth */}
          <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
          <Route path="/register" element={<AuthRoute><Register /></AuthRoute>} />

          {/* Applicant flow */}
          <Route path="/profile/complete" element={<PrivateRoute><ProfileComplete /></PrivateRoute>} />
          <Route path="/membership/apply" element={<PrivateRoute><MembershipApply /></PrivateRoute>} />
          <Route path="/membership/status" element={<PrivateRoute><MembershipStatus /></PrivateRoute>} />

          {/* Member */}
          <Route path="/dashboard" element={<MemberRoute><MemberDashboard /></MemberRoute>} />
          <Route path="/dashboard/listings" element={<MemberRoute><MyListings /></MemberRoute>} />
          <Route path="/dashboard/listings/create" element={<MemberRoute><CreateListing /></MemberRoute>} />
          <Route path="/dashboard/listings/:id/edit" element={<MemberRoute><EditListing /></MemberRoute>} />
          <Route path="/dashboard/enquiries" element={<MemberRoute><Enquiries /></MemberRoute>} />
          <Route path="/dashboard/transactions" element={<MemberRoute><MemberTransactions /></MemberRoute>} />
          <Route path="/dashboard/profile" element={<MemberRoute><MemberProfile /></MemberRoute>} />

          {/* Admin */}
          <Route path="/admin" element={<AdminRoute><AdminOverview /></AdminRoute>} />
          <Route path="/admin/applications" element={<AdminRoute><Applications /></AdminRoute>} />
          <Route path="/admin/members" element={<AdminRoute><Members /></AdminRoute>} />
          <Route path="/admin/members/:id" element={<AdminRoute><MemberDetail /></AdminRoute>} />
          <Route path="/admin/listings" element={<AdminRoute><AllListings /></AdminRoute>} />
          <Route path="/admin/categories" element={<AdminRoute><Categories /></AdminRoute>} />
          <Route path="/admin/listing-types" element={<AdminRoute><ListingTypes /></AdminRoute>} />
          <Route path="/admin/config" element={<AdminRoute><Config /></AdminRoute>} />
          <Route path="/admin/site-content" element={<AdminRoute><SiteContent /></AdminRoute>} />
          <Route path="/admin/notifications" element={<AdminRoute><NotificationManager /></AdminRoute>} />
          <Route path="/admin/conversations" element={<AdminRoute><Conversations /></AdminRoute>} />
          <Route path="/admin/blocked-words" element={<AdminRoute><BlockedWords /></AdminRoute>} />
          <Route path="/admin/transactions" element={<AdminRoute><AdminTransactions /></AdminRoute>} />

          {/* Utility */}
          <Route path="/access-denied" element={<AccessDenied />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
